/**
 * The pure sizing arithmetic behind `Image`: what widths the srcset offers, what `sizes` tells the
 * browser, and what dimensions actually reach the derivative URL.
 *
 * Its own module because it is where this component touches the CDN's cache key, and that deserves a
 * test that does not need React, a router context or a DOM to run.
 */

/**
 * The grid every REQUESTED dimension is snapped to, and the reason this file has to care about the
 * CDN at all: the derivative URL is CloudFront's cache key, so two requests for the same picture a
 * pixel apart are two objects — fetched from imgproxy separately at every POP, and each competing for
 * the same LRU space that decides how long either survives.
 *
 * That is measured, not theoretical. One live portal page asked for `w:381` AND `w:380` of a single
 * logo, and `h:222/w:320` alongside `h:220/w:320` of a single photo. Nothing varied but the caller's
 * `aspectRatio`, which differs in the third decimal between two components drawing the same image and
 * lands `Math.round` on adjacent pixels.
 *
 * 8 rather than 4 because it collapses roughly four times as many neighbours, and the worst case is a
 * 4px deviation — under half a percent at the sizes served here, on a bitmap the browser then scales
 * into a CSS box it sized itself. Nearest rather than down, so a request never asks for FEWER pixels
 * than a high-density screen wants; asking for a few more is free, since imgproxy clamps to the source
 * and never enlarges.
 *
 * This snaps the REQUEST only. The `width`/`height` attributes `Image` renders keep their exact
 * values, because those are the CLS reservation and the layout — moving them to an 8px grid would
 * shift the page to save bytes somewhere the reader cannot see.
 */
export const DIMENSION_STEP = 8;

export function snapDimension(value: number | undefined): number | undefined {
	if (value === undefined || value <= 0) return value;
	return Math.max(DIMENSION_STEP, Math.round(value / DIMENSION_STEP) * DIMENSION_STEP);
}

/**
 * Anchor breakpoints on the actual display width: 1×, 1.5× and 2× cover all device pixel ratios in
 * use today with ≥25% steps between variants. 160 and 320 are kept as mobile-fallback floors. This
 * trims the srcset from 10–11 entries down to 4–5 per image, halving the bytes and the CDN cache
 * surface.
 *
 * The BASE is snapped before the multiples are derived from it, not merely the results: snapping only
 * at the end still splits the 1.5× step, because 380 and 381 round to 570 and 572 first and those sit
 * either side of a grid line (568 against 576). Snapping the base makes two callers a pixel apart
 * agree on the WHOLE srcset rather than on three quarters of it. Deduped after, since two steps can
 * now land on one width, and the same URL twice under different `w` descriptors says nothing twice.
 */
export function getBreakpoints(width: number): number[] {
	const base = snapDimension(width) ?? width;
	const candidates = [160, 320, base, snapDimension(Math.round(base * 1.5)) ?? base, base * 2].filter(
		(w) => w <= base * 2,
	);
	return [...new Set(candidates)].sort((a, b) => a - b);
}

/** Generate a stepped `sizes` from breakpoints so the browser doesn't assume 100vw. */
export function getSizes(width: number, breakpoints: number[]): string {
	const steps = [...new Set(breakpoints)].filter((bp) => bp <= width).sort((a, b) => b - a);
	return steps.map((bp, i) => (i === steps.length - 1 ? `${bp}px` : `(min-width: ${bp}px) ${bp}px`)).join(', ');
}
